/**
 * Composant de prompt d'installation PWA contextuel
 *
 * Affiche une bannière flottante pour guider les utilisateurs à installer
 * l'application Brydge. Utilise le composant InstallInstructions partagé.
 *
 * Fonctionnalités :
 * - Logique de déclenchement intelligente (cooldown, compteur de refus)
 * - Ne s'affiche pas si déjà installé
 * - Animation Material-UI fluide
 *
 * @module InstallPrompt
 * @author Équipe Brydge
 * @since 1.0.0
 * @requires @mui/material
 * @requires react
 */

import React, {useState, useEffect} from 'react';
import {Box, IconButton, Paper, Typography, Slide, Stack} from '@mui/material';
import {Close as CloseIcon, GetApp as InstallIcon} from '@mui/icons-material';
import {useInstallPrompt} from '../hooks/useInstallPrompt';
import {isStandalone} from '../utils/detectPlatform';
import InstallInstructions from './InstallInstructions';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const {platform, deferredPrompt, handleInstall} = useInstallPrompt();

  useEffect(() => {
    // Vérifier si l'app a déjà été installée
    const wasInstalled = localStorage.getItem('pwa_was_installed');
    if (wasInstalled === 'true') {
      return;
    }

    // Ne pas afficher si actuellement en mode standalone
    if (isStandalone()) {
      return;
    }

    // Vérifier le nombre de fois que l'utilisateur a refusé
    const declineCount = parseInt(localStorage.getItem('pwa_install_decline_count') || '0', 10);
    const lastDeclineTime = parseInt(localStorage.getItem('pwa_install_last_decline') || '0', 10);
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;

    // Après 3 refus, ne plus jamais afficher
    if (declineCount >= 3) {
      return;
    }

    // Entre 1-2 refus, attendre 7 jours
    if (declineCount > 0 && now - lastDeclineTime < oneDayMs * 7) {
      return;
    }

    // Afficher le prompt après 3 secondes
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShowPrompt(false);

    // Incrémenter le compteur de refus
    const currentCount = parseInt(localStorage.getItem('pwa_install_decline_count') || '0', 10);
    localStorage.setItem('pwa_install_decline_count', String(currentCount + 1));
    localStorage.setItem('pwa_install_last_decline', String(Date.now()));
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      const result = await handleInstall();
      if (result === 'accepted') {
        localStorage.setItem('pwa_was_installed', 'true');
        setShowPrompt(false);
      }
    }
    // Pour iOS, les instructions sont déjà affichées
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1300,
          maxWidth: 600,
          margin: '0 auto',
          p: 3,
          backgroundColor: '#ffffff',
          borderRadius: 3,
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Stack spacing={2.5}>
          {/* Header */}
          <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <InstallIcon sx={{color: '#fff', fontSize: 24}} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{color: 'text.primary'}}>
                  Installer Brydge
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{mt: -0.5, mr: -0.5}}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.6}}>
            Installez Brydge sur votre{' '}
            {platform === 'ios' ?
              'iPhone/iPad' :
              platform === 'android' ?
              'appareil' :
              'ordinateur'}{' '}
            pour un accès rapide et une expérience optimale.
          </Typography>

          {/* Instructions réutilisables - mode compact */}
          <InstallInstructions
            deferredPrompt={deferredPrompt}
            onInstall={handleInstallClick}
            compact={true}
          />
        </Stack>
      </Paper>
    </Slide>
  );
};

export default InstallPrompt;
